import React, { useEffect, useRef } from "react";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import {
  PolicyPageWrapper,
  PolicyHeroSection,
  PolicyContainer,
} from "../Policy/styles";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const TermsOfService = () => {
  const heroRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        hero.querySelector(".heroLabel"),
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", delay: 0.1 }
      );

      gsap.fromTo(
        hero.querySelector("h1"),
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", delay: 0.2 }
      );

      gsap.fromTo(
        hero.querySelector(".heroDescription"),
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out", delay: 0.4 }
      );
    }, hero);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const ctx = gsap.context(() => {
      const sections = content.querySelectorAll(".content-section");
      sections.forEach((section) => {
        gsap.fromTo(
          section,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power3.out",
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });
    }, content);

    return () => ctx.revert();
  }, []);

  return (
    <PolicyPageWrapper>
      <Header />

      <PolicyHeroSection ref={heroRef}>
        <div className="heroBg">
          <div className="gradientOrb orb1" />
          <div className="gradientOrb orb2" />
          <div className="gridPattern" />
        </div>

        <div className="floatingShape shape1" />
        <div className="floatingShape shape2" />
        <div className="floatingShape shape3" />

        <div className="heroContent">
          <div className="heroLabel">
            <span className="line" />
            <span>Legal</span>
            <span className="line" />
          </div>

          <h1>
            Terms of <span className="highlight">Service</span>
          </h1>

          <p className="heroDescription">
            Please read these terms carefully before using our services. By
            accessing our website, you agree to these terms and conditions.
          </p>
        </div>
      </PolicyHeroSection>

      <PolicyContainer>
        <ContentWrapper ref={contentRef}>
          <ContentSection className="content-section">
            <h2>Acceptance of Terms</h2>
            <p>
              By accessing and using the Sahara Computers website and services,
              you accept and agree to be bound by these Terms of Service. If you
              do not agree to these terms, please do not use our services.
            </p>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Products and Services</h2>
            <p>
              Sahara Computers provides IT hardware, software, and technical
              support services. All products and services are subject to
              availability and may be modified or discontinued without prior
              notice.
            </p>
            <ul>
              <li>Product descriptions and specifications are provided for informational purposes</li>
              <li>Prices are subject to change without notice</li>
              <li>Images are for illustration purposes and may vary from actual products</li>
              <li>We reserve the right to limit quantities on any order</li>
            </ul>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Orders and Payments</h2>
            <p>When placing an order with Sahara Computers:</p>
            <ul>
              <li>All orders are subject to acceptance and availability</li>
              <li>We reserve the right to refuse or cancel any order</li>
              <li>Payment must be made in full before delivery or service</li>
              <li>Prices are quoted in Kuwaiti Dinar (KWD) unless otherwise specified</li>
              <li>Applicable taxes and delivery charges will be added to your order</li>
            </ul>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Warranty Policy</h2>
            <p>
              Products sold by Sahara Computers are covered by manufacturer
              warranties. Our warranty terms include:
            </p>
            <ul>
              <li>Warranty periods vary by product and manufacturer</li>
              <li>Warranty covers manufacturing defects only</li>
              <li>Physical damage, misuse, or unauthorized modifications void the warranty</li>
              <li>Original proof of purchase is required for warranty claims</li>
              <li>Warranty service may be provided through repair or replacement at our discretion</li>
            </ul>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Service Terms</h2>
            <HighlightBox>
              <h3>No Solution, No Charge Policy</h3>
              <p>
                If we are unable to resolve your computer problems, there will be
                no charge for our services. However, certain cases may require
                upgrades or replacements, and in such instances, charges may apply.
              </p>
            </HighlightBox>
            <p>Additional service terms:</p>
            <ul>
              <li>Onsite services are warranted for 30 days</li>
              <li>Service appointments are subject to availability</li>
              <li>Customer must provide necessary access and information for service</li>
              <li>Data backup is the customer's responsibility before any service</li>
            </ul>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Returns and Refunds</h2>
            <p>Our return policy includes the following conditions:</p>
            <ul>
              <li>Returns must be requested within 7 days of purchase</li>
              <li>Products must be in original condition with all packaging</li>
              <li>Software and consumables are non-returnable once opened</li>
              <li>Refunds will be processed within 14 business days</li>
              <li>Shipping charges are non-refundable</li>
            </ul>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Limitation of Liability</h2>
            <p>
              Sahara Computers shall not be liable for any indirect, incidental,
              special, or consequential damages arising from the use of our
              products or services. Our total liability shall not exceed the
              amount paid for the specific product or service in question.
            </p>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Intellectual Property</h2>
            <p>
              All content on this website, including text, graphics, logos, and
              images, is the property of Sahara Computers or its licensors and is
              protected by copyright and trademark laws. Unauthorized use is
              prohibited.
            </p>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Governing Law</h2>
            <p>
              These Terms of Service shall be governed by and construed in
              accordance with the laws of the State of Kuwait. Any disputes shall
              be subject to the exclusive jurisdiction of the courts of Kuwait.
            </p>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Contact Information</h2>
            <p>
              For questions about these Terms of Service, please contact us at:
            </p>
            <ContactInfo>
              <div className="contact-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <span>sales@saharacomputers.com</span>
              </div>
            </ContactInfo>
          </ContentSection>

          <LastUpdated>Last updated: January 2024</LastUpdated>
        </ContentWrapper>
      </PolicyContainer>

      <Footer />
    </PolicyPageWrapper>
  );
};

export default TermsOfService;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 800px;
`;

const ContentSection = styled.div`
  margin-bottom: 48px;
  will-change: transform, opacity;

  h2 {
    font-family: Poppins;
    font-size: 28px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 20px;
    position: relative;
    padding-left: 20px;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 8px;
      width: 4px;
      height: 24px;
      background: var(--accent);
      border-radius: 2px;
    }
  }

  p {
    font-family: Poppins;
    font-size: 16px;
    line-height: 1.8;
    color: var(--text-secondary);
    margin: 0 0 16px;
  }

  ul {
    margin: 0 0 20px;
    padding-left: 24px;

    li {
      font-family: Poppins;
      font-size: 16px;
      line-height: 1.8;
      color: var(--text-secondary);
      margin-bottom: 8px;

      &::marker {
        color: var(--accent);
      }
    }
  }
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, rgba(217, 58, 53, 0.1) 0%, rgba(43, 59, 124, 0.1) 100%);
  border: 2px solid var(--accent);
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 24px;

  h3 {
    font-family: Poppins;
    font-size: 20px;
    font-weight: 700;
    color: var(--accent);
    margin: 0 0 12px;
  }

  p {
    margin: 0;
  }
`;

const ContactInfo = styled.div`
  margin-top: 20px;

  .contact-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    background: var(--card-bg);
    border-radius: 12px;
    border: 2px solid var(--card-border);

    svg {
      width: 24px;
      height: 24px;
      color: var(--accent);
    }

    span {
      font-family: Poppins;
      font-size: 16px;
      font-weight: 500;
      color: var(--text-primary);
    }
  }
`;

const LastUpdated = styled.div`
  font-family: Poppins;
  font-size: 14px;
  color: var(--text-secondary);
  text-align: center;
  padding-top: 40px;
  border-top: 1px solid var(--card-border);
`;
